import { z } from "@brains/utils/zod";
import type { UserPermissionLevel } from "@brains/templates";
import type {
  MessageValidationResult,
  MessageValidationSchema,
} from "./message-validator";
import type {
  MessageResponse,
  MessageHandler,
  BaseMessage,
  MessageWithPayload,
  MessageBusSendRequest,
} from "./base-types";

export {
  messageResponseSchema,
  messageWithPayloadSchema,
  baseMessageSchema,
  type MessageResponse,
  type MessageWithPayload,
  type MessageHandler,
  type MessageSender,
  type MessageSendOptions,
  type MessageSendRequest,
  type MessageBusSendRequest,
  type BaseMessage,
} from "./base-types";

/**
 * Internal message bus response schema (with more details than the simple MessageResponse)
 * This is used internally by the message bus for tracking and debugging
 */
export const internalMessageResponseSchema: z.ZodObject<{
  id: z.ZodString;
  requestId: z.ZodString;
  success: z.ZodBoolean;
  data: z.ZodOptional<z.ZodUnknown>;
  error: z.ZodOptional<
    z.ZodObject<{ message: z.ZodString; code: z.ZodOptional<z.ZodString> }>
  >;
  timestamp: z.ZodString;
}> = z.object({
  id: z.string().min(1),
  requestId: z.string().min(1),
  success: z.boolean(),
  data: z.unknown().optional(),
  error: z
    .object({
      message: z.string(),
      code: z.string().optional(),
    })
    .optional(),
  timestamp: z.string().datetime(),
});

export type InternalMessageResponse = z.output<
  typeof internalMessageResponseSchema
>;

/**
 * Type guard to check if a message has a payload
 */
export function hasPayload<P = unknown>(
  message: BaseMessage,
): message is BaseMessage & { payload: P } {
  return "payload" in message;
}

/**
 * Subscription filter for targeted message delivery
 */
export interface SubscriptionFilter {
  source?: string | RegExp;
  target?: string | RegExp;
  metadata?: Record<string, unknown>;
  predicate?: (message: MessageWithPayload) => boolean;
}

/**
 * Message context for interface operations
 * Used by message-based interfaces to provide context about the message
 */
export interface MessageContext {
  userId: string;
  channelId: string;
  messageId: string;
  timestamp: Date;
  interfaceType: string;
  userPermissionLevel: UserPermissionLevel;
  threadId?: string;
}

/**
 * Message bus interface
 */
export interface IMessageBus {
  send<T = unknown, R = unknown>(
    request: MessageBusSendRequest<T>,
  ): Promise<MessageResponse<R>>;

  subscribe<T = unknown, R = unknown>(
    type: string,
    handler: MessageHandler<T, R>,
    filter?: SubscriptionFilter,
  ): () => void;

  /** Collect one response from every matching handler, in registration order. */
  collect<T = unknown, R = unknown>(
    request: MessageBusSendRequest<T>,
  ): Promise<MessageResponse<R>[]>;

  validateMessage<T>(
    message: unknown,
    schema: MessageValidationSchema<T>,
  ): MessageValidationResult<T>;

  unsubscribe<T = unknown, R = unknown>(
    type: string,
    handler: MessageHandler<T, R>,
  ): void;

  /*
   * Handler introspection.
   *
   * These describe the bus the way `hasHandlers` already did, and they are
   * what a caller checking wiring reaches for. They were missing here while
   * the only implementation had them, so consumers took the `MessageBus` class
   * instead — and a test double then had to be asserted into a type with
   * private state.
   */
  hasHandlers?(type: string): boolean;
  getHandlerCount(messageType: string): number;
  getTargetedHandlerCount(messageType: string, target: string): number;
  clearHandlers(messageType: string): void;
  clearAllHandlers(): void;
}
